import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Paper, Typography, CircularProgress } from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { DateTime } from 'luxon';
import { useDispatch, useSelector } from 'react-redux';
import { IRootState } from 'stores';
import { BankingState, setBalances } from 'stores/banking';
import { BankingService } from 'services';
import { useSnackbar } from 'notistack';
import { EAccountType } from 'common/accounts.interface';
import Actions from './components/Actions';
import Filters from './components/Filters';
import Table from './components/Table';
import { usePaginatedBankSlips } from './hooks/usePaginatedBankSlips';
import { DashboardTypeSelector } from '../../components/DashboardTypeSelector';
import { DashboardBalanceHeader } from '../../components/DashboardBalanceHeader';

const ReceivePage = () => {
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { accounts } = useSelector<IRootState, BankingState>(({ banking }) => banking);

  const [accountType] = useState(EAccountType.CONTROL);
  const selectedAccount = useMemo(() => accounts?.find((a: any) => a.accountType === accountType), [accounts, accountType]);

  // Initialize state from query params if present
  const getInitialStatus = () => {
    const kind = searchParams.get('kind');
    return kind ? 'PENDENTE' : 'Todos';
  };

  const getInitialType = () => {
    const kind = searchParams.get('kind');
    if (kind === 'boleto') return 'Boleto';
    return 'Todas';
  };

  const getInitialStartDate = () => {
    const startParam = searchParams.get('payDueDateStart');
    if (startParam) {
      try {
        return DateTime.fromISO(startParam);
      } catch {
        return DateTime.now().minus({ days: 30 });
      }
    }
    return DateTime.now().minus({ days: 30 });
  };

  const getInitialEndDate = () => {
    const endParam = searchParams.get('payDueDateEnd');
    if (endParam) {
      try {
        return DateTime.fromISO(endParam);
      } catch {
        return DateTime.now().plus({ months: 3 });
      }
    }
    return DateTime.now().plus({ months: 3 });
  };

  const [status, setStatus] = useState(getInitialStatus());
  const [type, setType] = useState(getInitialType());
  const [startDate, setStartDate] = useState(getInitialStartDate());
  const [endDate, setEndDate] = useState(getInitialEndDate());
  const [showDuplicates, setShowDuplicates] = useState(false);

  const { transactions, loading, page, rowsPerPage, total, setPage, setRowsPerPage } = usePaginatedBankSlips({
    status,
    type,
    startDate,
    endDate,
    useDueDateFilter: true,
  });

  // Reset page to 0 when showDuplicates changes
  useEffect(() => {
    setPage(0);
  }, [showDuplicates, setPage]);

  const filteredTransactions = React.useMemo(() => {
    if (showDuplicates) return transactions;
    const seen = new Set();
    return transactions.filter((t) => {
      const payerName = t.bankSlipDetail?.payerName || t.counterpartyName || '';
      const key = `${payerName}-${t.value}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }, [transactions, showDuplicates]);

  const loadBalances = useCallback(async () => {
    if (!accounts) return;

    try {
      const bankingService = new BankingService();
      const balances = await bankingService.getAccountsBalance();
      dispatch(setBalances(balances));
    } catch {
      enqueueSnackbar('Erro ao carregar saldos das contas', {
        variant: 'error',
      });
    }
  }, [accounts, dispatch, enqueueSnackbar]);

  useEffect(() => {
    loadBalances();
  }, [loadBalances]);

  return (
    <Box display="flex" flexDirection="column">
      <DashboardTypeSelector selectedAccount={accountType} />

      <DashboardBalanceHeader account={selectedAccount} />

      <Actions />

      <Typography sx={{ color: 'text.primary', fontSize: '24px', marginBottom: '2vh', fontWeight: 'medium' }}>Recebimentos</Typography>

      <Paper sx={{ p: 3, borderRadius: '12px', border: '1px solid #F2F4F8' }} elevation={0}>
        <Filters
          status={status}
          type={type}
          startDate={startDate}
          endDate={endDate}
          onStatusChange={setStatus}
          onStartDateChange={setStartDate}
          onEndDateChange={setEndDate}
          onTypeChange={setType}
          showDuplicates={showDuplicates}
          onShowDuplicatesChange={setShowDuplicates}
          startDateLabel="Vencimento inicial"
          endDateLabel="Vencimento final"
          enableMaxDateValidation={false}
        />

        <Box mt={3}>
          {loading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : (
            <Table
              transactions={filteredTransactions}
              page={page}
              rowsPerPage={rowsPerPage}
              total={showDuplicates ? total : filteredTransactions.length}
              onPageChange={setPage}
              onRowsPerPageChange={setRowsPerPage}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ReceivePage;
